// @ts-nocheck

/**
 * CallHistoryScreen — Historique (audit B2-call-history, W4).
 *
 * Responsibility: every call of the organisation (and of the selected
 * customer), one server page at a time.
 * - Data: `POST /api/entities/ParallelCall/page` (cursor, include_total),
 *   filtered on the server by `buildHistoryFilter` (callHistoryQuery.ts):
 *   period (7 days by default), status, list, minimum duration, direction,
 *   user, search. It used to filter the 500 calls the dialer had loaded
 *   (T-d3), without saying so.
 * - Table: the shared DataTable (sort on the start date, hideable columns,
 *   filter pills, loading / empty / filtered / error states).
 * - CSV export: every page of the current filter, up to EXPORT_CAP rows;
 *   past it, the export says how many rows it left out (never silent).
 * - « Corriger » (ADR-047 §4, DES-17): for 24 h after the commit, a call
 *   qualified through the call page can be corrected from here
 *   (CorrectQualificationDialog).
 * - A row opens the contact's record; `?call=<id>` reopens the call detail.
 * Docs: docs/refonte-ui/audit-ui-ux.md (Historique), DESIGN.md §4.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PERIOD_VALUES,
  STATUS_TONE,
  STATUS_VALUES,
  MIN_DURATION_OPTIONS,
  getPeriodRange,
  resolveCallStatus,
  resolveDirection,
  resolveDate,
} from '@/services/dialer/callHistory';
import { buildHistoryFilter, DEFAULT_HISTORY_PERIOD } from '@/services/dialer/callHistoryQuery';
import { canOfferCorrection } from '@/services/dialer/qualificationCorrection';
import { recordingPlaybackUrl } from '@/services/dialer/recordingUrl';
import {
  Search, Download, Phone, PhoneIncoming, PhoneOutgoing,
  Filter, ChevronDown, X, Check, Calendar,
  Clock, FileText, Link2, Sparkles, User,
} from 'lucide-react';
// #76 + #31 -- la rubrique "objections detectees" de la pop-up lit la meme
// detection que le module Objections : une seule source.
import { detecterObjections } from '@/lib/objections';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/shared/DataTable';
import { RecordingCell } from '@/components/dialer/RecordingPlayer';
import { CorrectQualificationDialog } from '@/components/dialer/CorrectQualificationDialog';
import { useDialerDerived } from '@/contexts/DialerDerivedContext';
import { useDialerData } from '@/contexts/DialerDataContext';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from '@/utils';
import { getEffectiveOutcome } from '@/utils/callUtils';
import ProspectDetailPanel from '@/components/dialer/ProspectDetailPanel';
import CallScreeningTag from '@/components/dialer/CallScreeningTag';
import { currentLocale, formatDateTime, formatDurationClock } from '@/i18n/locale';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { callFailureLabel } from '@/services/dialer/callFailureReason';
import { usePermissions } from '@/hooks/usePermissions';
import { useMembers } from '@/hooks/queries/useAdmin';
import { useListsPhonesBeyondCap } from '@/hooks/useProspectListBeyondCap';
import { useAuthStore } from '@/stores/authStore';
import { leadStatusLabel } from '@/services/dialer/leadStatus';
import { toCsv, downloadCsv } from '@/lib/exportCsv';
import { CopyLink, copyText } from '@/components/shared/CopyLink';
import { apiClient, ApiErrorException } from '@/api/client';
import { fetchEntityPage, fetchAllPages } from '@/api/endpoints/entityPage';
import { toast } from 'sonner';

export const HISTORY_PAGE_SIZE = 50;
/** An export reads at most this many calls; past it, it says how many it left out (T-d3). */
export const EXPORT_CAP = 5000;

function DurationCell({ call }) {
  const stored = Number(call.duration_seconds || 0);
  const hasRecording = !!call.recording_url;
  const shouldProbe = hasRecording && stored <= 0;
  const [probed, setProbed] = useState(null);

  useEffect(() => {
    if (!shouldProbe) return;
    const audio = new Audio();
    audio.preload = 'metadata';
    const onLoaded = () => {
      const d = Number(audio.duration);
      if (Number.isFinite(d) && d > 0) setProbed(d);
    };
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.src = recordingPlaybackUrl(call.recording_url);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.src = '';
    };
  }, [shouldProbe, call.recording_url]);

  const value = stored > 0 ? stored : probed;
  return <>{formatDurationClock(value)}</>;
}

function StatusBadge({ status, t }) {
  const tone = STATUS_TONE[status];
  if (!tone) {
    return (
      <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium bg-muted/50 text-muted-foreground border-border">
        {status || '—'}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${tone}`}>
      {outcomeLabel(status, t)}
    </span>
  );
}

function DirectionIcon({ direction, t }) {
  const label = direction === 'inbound' ? t('dialer:callHistory.direction.inbound')
    : direction === 'outbound' ? t('dialer:callHistory.direction.outbound')
      : t('dialer:callHistory.direction.unknown');
  const Icon = direction === 'inbound' ? PhoneIncoming : direction === 'outbound' ? PhoneOutgoing : Phone;
  const color = direction === 'inbound' ? 'text-blue-500' : direction === 'outbound' ? 'text-emerald-600' : 'text-muted-foreground';
  return (
    <span title={label} className={`inline-flex items-center ${color}`}>
      <Icon className="w-3.5 h-3.5" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Start and end of a call; the end is deduced from the duration when unknown (#75). */
function StartEnd({ call }) {
  const dateStr = resolveDate(call);
  const debut = dateStr ? new Date(dateStr) : null;
  const secondes = Number(call.duration_seconds || 0);
  let fin = call.ended_at ? new Date(call.ended_at) : null;
  if ((!fin || isNaN(fin.getTime()) || (debut && fin.getTime() <= debut.getTime())) && debut && secondes > 0) {
    fin = new Date(debut.getTime() + secondes * 1000);
  }
  // Under a minute, seconds on both sides: « 12:01 → 12:01 » reads as a bug.
  const court = secondes > 0 && secondes < 60;
  const heure = (d) => {
    if (!d || isNaN(d.getTime()) || !debut || d.getTime() <= debut.getTime()) return null;
    return d.toLocaleTimeString(currentLocale(), court ? { hour: '2-digit', minute: '2-digit', second: '2-digit' } : { hour: '2-digit', minute: '2-digit' });
  };
  return (
    <div className="flex flex-col leading-tight whitespace-nowrap">
      <span className="text-xs text-muted-foreground tabular-nums">
        {court && debut && !isNaN(debut.getTime())
          ? debut.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' })
            + ' ' + debut.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' })
          : formatDateTime(dateStr)}
      </span>
      {heure(fin) && <span className="text-[11px] text-muted-foreground/70 tabular-nums">→ {heure(fin)}</span>}
    </div>
  );
}

// ─── Multi-select status popover ─────────────────────────────────────────────

function StatusFilterPopover({ selectedStatuses, onChange, statusOptions, t }) {
  const allSelected = selectedStatuses.length === 0;
  const toggle = (value) => onChange(selectedStatuses.includes(value) ? selectedStatuses.filter((s) => s !== value) : [...selectedStatuses, value]);
  const label = allSelected
    ? t('dialer:callHistory.filters.allStatuses')
    : selectedStatuses.length === 1
      ? outcomeLabel(selectedStatuses[0], t)
      : t('dialer:callHistory.filters.statusCount', { count: selectedStatuses.length });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-transparent text-sm hover:bg-accent transition-colors whitespace-nowrap">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />
          <span>{label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-0.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <button type="button" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent transition-colors" onClick={() => onChange([])}>
          <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${allSelected ? 'bg-primary border-primary' : 'border-input'}`}>
            {allSelected && <Check className="w-3 h-3 text-primary-foreground" aria-hidden />}
          </div>
          <span>{t('dialer:callHistory.filters.allStatuses')}</span>
        </button>
        <div className="my-1 h-px bg-border" />
        {statusOptions.map(({ value, label: optLabel }) => (
          <button key={value} type="button" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent transition-colors" onClick={() => toggle(value)}>
            <Checkbox checked={selectedStatuses.includes(value)} onCheckedChange={() => toggle(value)} className="pointer-events-none" />
            <span>{optLabel}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ─── List filter popover ──────────────────────────────────────────────────────

function ListFilterPopover({ sources, prospectsBySource, selectedSources, onChange, t }) {
  const [search, setSearch] = useState('');
  const filteredSources = useMemo(() => sources.filter((s) => s.toLowerCase().includes(search.toLowerCase())), [sources, search]);
  const allSelected = selectedSources.length === 0;
  const toggle = (src) => onChange(selectedSources.includes(src) ? selectedSources.filter((s) => s !== src) : [...selectedSources, src]);
  const label = allSelected
    ? t('dialer:callHistory.filters.allLists')
    : selectedSources.length === 1
      ? selectedSources[0]
      : t('dialer:callHistory.filters.listCount', { count: selectedSources.length });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-transparent text-sm hover:bg-accent transition-colors whitespace-nowrap">
          <span>{label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-0.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="mb-2">
          <Input placeholder={t('common:actions.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-7 text-xs" />
        </div>
        <button type="button" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent transition-colors" onClick={() => onChange([])}>
          <div className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${allSelected ? 'bg-primary border-primary' : 'border-input'}`}>
            {allSelected && <Check className="w-3 h-3 text-primary-foreground" aria-hidden />}
          </div>
          <span>{t('dialer:callHistory.filters.selectAll')}</span>
        </button>
        <div className="my-1 h-px bg-border" />
        <div className="max-h-52 overflow-y-auto space-y-0.5">
          {filteredSources.map((src) => (
            <button key={src} type="button" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent transition-colors" onClick={() => toggle(src)}>
              <Checkbox checked={selectedSources.includes(src)} onCheckedChange={() => toggle(src)} className="pointer-events-none shrink-0" />
              <span className="flex-1 text-left truncate">{src}</span>
              <span className="text-xs text-muted-foreground shrink-0">{(prospectsBySource[src] || []).length}</span>
            </button>
          ))}
          {filteredSources.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-2">{t('dialer:callHistory.filters.noListsFound')}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const errorCode = (err) => (err instanceof ApiErrorException ? err.code : err ? 'NETWORK' : undefined);

// ─── Main screen ─────────────────────────────────────────────────────────────

export default function CallHistoryScreen() {
  const { t } = useTranslation(['pages', 'dialer', 'common']);
  const navigate = useNavigate();
  const { isTenantAdmin } = usePermissions();
  // ADR-024 -- customer-scoped history: the server filters on the selected customer.
  const { filteredAllCalls: loadedCalls = [], prospectsBySource, sources, filteredProspectQueue: prospectQueue = [] } = useDialerDerived();
  const { selectedCustomerId, prospectQueue: engineQueue } = useDialerData();
  // An admin supervises a team: the user column and filter (audit B2, point 2).
  const activeOrgId = useAuthStore((s) => s.activeMembership?.organisationId);
  const { data: members = [] } = useMembers(isTenantAdmin ? activeOrgId : undefined);
  const users = useMemo(
    () => members.filter((m) => m.is_active !== false && m.role !== 'viewer')
      .map((m) => ({ email: m.email, name: m.full_name || m.username || m.email })),
    [members],
  );
  const showUsers = isTenantAdmin && users.length > 0;

  const periodLabel = useCallback((value) => t(`dialer:callHistory.period.${value.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())}`), [t]);
  const periodOptions = useMemo(() => PERIOD_VALUES.map((value) => ({ value, label: periodLabel(value) })), [periodLabel]);
  const statusOptions = useMemo(() => STATUS_VALUES.map((value) => ({ value, label: outcomeLabel(value, t) })), [t]);
  const statusLabelOf = useCallback((s) => (STATUS_TONE[s] ? outcomeLabel(s, t) : s), [t]);

  // ── Contact record and call detail ─────────────────────────────────────────
  const [selectedProspectForDetail, setSelectedProspectForDetail] = useState(null);
  const prospectByPhone = useMemo(() => {
    const m = new Map();
    prospectQueue.forEach((p) => { if (p.prospect_phone && !m.has(p.prospect_phone)) m.set(p.prospect_phone, p); });
    return m;
  }, [prospectQueue]);
  // A row opens the contact's record (Kevin, 2026-09-30); a contact no longer
  // in the queue is rebuilt from the call.
  const ouvrirFiche = (call) => {
    setSelectedProspectForDetail(prospectByPhone.get(call.prospect_phone) || {
      id: call.id,
      prospect_name: call.prospect_name,
      prospect_phone: call.prospect_phone,
      prospect_company: call.prospect_company,
      prospect_title: call.prospect_title,
      prospect_email: call.prospect_email,
    });
  };
  const [callDetail, setCallDetail] = useState(null);
  const [toCorrect, setToCorrect] = useState(null);
  // REV-W4-F4: a correction that committed refreshes the call detail if it
  // shows that call (it is local state, not a query).
  const callDetailRef = useRef(null);
  callDetailRef.current = callDetail;
  const refreshCallDetail = useCallback((id) => {
    if (callDetailRef.current?.id !== id) return;
    apiClient.get(`/entities/ParallelCall/${encodeURIComponent(id)}`)
      .then((c) => { if (c && c.id) setCallDetail((cur) => (cur && cur.id === c.id ? c : cur)); })
      .catch(() => { /* the detail keeps what it showed */ });
  }, []);
  const [idLienCopie, setIdLienCopie] = useState(null);
  const lienAppel = useCallback(
    (id) => `${window.location.origin}${window.location.pathname}?call=${encodeURIComponent(id)}`,
    [],
  );

  // #76 -- ?call=<id> reopens the call detail, wherever the call is in the history.
  const dejaOuvertParUrl = useRef(false);
  useEffect(() => {
    if (dejaOuvertParUrl.current) return;
    const id = new URLSearchParams(window.location.search).get('call');
    if (!id) return;
    dejaOuvertParUrl.current = true;
    apiClient.get(`/entities/ParallelCall/${encodeURIComponent(id)}`)
      .then((c) => { if (c && c.id) setCallDetail(c); })
      .catch(() => toast.error(t('dialer:callHistory.detail.notFound')));
  }, [t]);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [period, setPeriod]               = useState(DEFAULT_HISTORY_PERIOD);
  const [customStart, setCustomStart]     = useState('');
  const [customEnd, setCustomEnd]         = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedSources, setSelectedSources]   = useState([]);
  const [minDuration, setMinDuration]     = useState('0');
  // Paramètres › Appels entrants links here with ?direction=inbound.
  const [searchParams] = useSearchParams();
  const [direction, setDirection]         = useState(() => {
    const d = searchParams.get('direction');
    return d === 'inbound' || d === 'outbound' ? d : 'all';
  });
  const [agentEmail, setAgentEmail]       = useState('');
  const [searchInput, setSearchInput]     = useState('');
  const [search, setSearch]               = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // REV-W4-F6: « par liste » took the phones from the engine's queue, capped
  // at ENGINE_QUEUE_LIMIT: past it, the older contacts' calls silently fell
  // out of the filter and the CSV (T-d3). When the queue is full the chosen
  // lists are read through the paginated route; bounded reads say « N sur T ».
  // The phones are sorted: the queue's 5 s poll rebuilds the set in another
  // order, which used to change the filter and send the table back to page 1.
  const listsBeyondCap = useListsPhonesBeyondCap({
    sources: selectedSources,
    customerId: selectedCustomerId,
    engineQueueLength: (engineQueue || []).length,
  });
  const listPhones = useMemo(() => {
    if (selectedSources.length === 0) return null;
    const phones = new Set();
    selectedSources.forEach((src) => (prospectsBySource?.[src] || []).forEach((p) => { if (p.prospect_phone) phones.add(p.prospect_phone); }));
    if (listsBeyondCap.status === 'ready') listsBeyondCap.data.phones.forEach((p) => phones.add(p));
    return [...phones].sort();
  }, [selectedSources, prospectsBySource, listsBeyondCap.status, listsBeyondCap.data]);

  const filter = useMemo(() => buildHistoryFilter({
    period, customStart, customEnd, statuses: selectedStatuses, listPhones,
    minDuration: parseInt(minDuration, 10) || 0, direction, search, agentEmail, customerId: selectedCustomerId,
  }), [period, customStart, customEnd, selectedStatuses, listPhones, minDuration, direction, search, agentEmail, selectedCustomerId]);

  const [sort, setSort] = useState({ id: 'start', dir: 'desc' });
  const sortParam = sort?.dir === 'asc' ? 'created_date' : '-created_date';

  // ── Server pages (cursor) ─────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([undefined]);
  const filterKey = JSON.stringify([filter, sortParam]);
  useEffect(() => { setPage(0); setCursors([undefined]); }, [filterKey]);

  const query = useQuery({
    queryKey: ['parallel-calls', 'history-page', filter, sortParam, cursors[page] ?? null],
    queryFn: ({ signal }) => fetchEntityPage('ParallelCall', {
      filter, sort: sortParam, limit: HISTORY_PAGE_SIZE, include_total: true, ...(cursors[page] ? { cursor: cursors[page] } : {}),
    }, signal),
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.items ?? [];
  const total = typeof query.data?.total === 'number' ? query.data.total : null;
  // A cursor issued for another filter (or expired): back to the first page.
  useEffect(() => {
    if (errorCode(query.error) === 'INVALID_CURSOR' && page > 0) { setPage(0); setCursors([undefined]); }
  }, [query.error, page]);

  const onPageChange = (next) => {
    if (next > page) {
      const c = query.data?.next_cursor;
      if (!c) return;
      setCursors((prev) => { const copy = prev.slice(0, page + 1); copy[page + 1] = c; return copy; });
    }
    setPage(next);
  };

  // « Appels » column: the contact's projected attempt count (ENG-5), else
  // what the loaded calls say.
  const loadedPerPhone = useMemo(() => {
    const m = new Map();
    loadedCalls.forEach((c) => m.set(c.prospect_phone, (m.get(c.prospect_phone) || 0) + 1));
    return m;
  }, [loadedCalls]);
  const attemptsOf = (call) => prospectByPhone.get(call.prospect_phone)?.call_history?.attempt_count ?? loadedPerPhone.get(call.prospect_phone) ?? null;
  const linkedinOf = (call) => call.linkedin_url || prospectByPhone.get(call.prospect_phone)?.linkedin_url || '';

  // ── CSV export: every page of the filter, capped, never silently ─────────
  const [exporting, setExporting] = useState(null);
  const [exportCapped, setExportCapped] = useState(null);
  useEffect(() => { setExportCapped(null); }, [filterKey]);

  const handleExport = useCallback(async () => {
    setExporting({ loaded: 0, total });
    setExportCapped(null);
    let result;
    try {
      result = await fetchAllPages('ParallelCall', { filter, sort: sortParam }, {
        cap: EXPORT_CAP,
        onProgress: (loaded, tot) => setExporting({ loaded, total: tot }),
      });
    } catch (err) {
      setExporting(null);
      toast.error(t('dialer:callHistory.export.failed'), { description: errorCode(err) });
      return;
    }
    setExporting(null);
    // The 8 client columns (User..Full Transcription) keep their English
    // names: the client's own export contract (#33), not UI copy.
    const headers = [
      t('dialer:callHistory.csv.contact'), t('dialer:callHistory.csv.company'),
      t('dialer:callHistory.csv.linkedin'), t('dialer:callHistory.csv.callsCount'),
      t('dialer:callHistory.csv.status'), t('dialer:callHistory.csv.direction'),
      t('dialer:callHistory.csv.durationSeconds'), t('dialer:callHistory.csv.from'),
      t('dialer:callHistory.csv.to'), t('dialer:callHistory.csv.date'),
      t('dialer:callHistory.csv.aiSummary'),
      'User', 'Job Title', 'Email', 'Company Website', 'Lead Status',
      'End Date', 'Meeting Booked', 'Full Transcription',
    ];
    const finDe = (call) => {
      const debut = new Date(call.created_date || call.started_at || 0);
      const sec = Number(call.duration_seconds || 0);
      let fin = call.ended_at ? new Date(call.ended_at) : null;
      if ((!fin || isNaN(fin.getTime()) || fin <= debut) && !isNaN(debut.getTime()) && sec > 0) fin = new Date(debut.getTime() + sec * 1000);
      return fin && !isNaN(fin.getTime()) ? formatDateTime(fin.toISOString()) : '';
    };
    const lines = result.items.map((call) => {
      const p = prospectByPhone.get(call.prospect_phone) || {};
      return [
        call.prospect_name,
        call.prospect_company,
        linkedinOf(call),
        attemptsOf(call) ?? '',
        // #1 -- the label read on screen, the raw value for an unknown status.
        statusLabelOf(resolveCallStatus(call)),
        resolveDirection(call),
        call.duration_seconds ?? '',
        call.from_number || call.agent_number || '',
        call.prospect_phone || '',
        formatDateTime(resolveDate(call)),
        call.ai_summary || '',
        call.agent_name || call.agent_email || '',
        p.prospect_title || call.prospect_title || '',
        p.prospect_email || '',
        p.website || (p.custom_fields || {}).website || '',
        leadStatusLabel(p.lead_status, t),
        finDe(call),
        (call.meeting_booked === true || call.call_outcome === 'meeting_booked') ? 'Oui' : 'Non',
        call.transcription || '',
      ];
    });
    // #32 -- the file is named after the exported period.
    const [bDebut, bFin] = getPeriodRange(period, customStart, customEnd);
    const jour = (d) => (d ? new Date(d).toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-') : null);
    const nom = (bDebut && bFin) ? `Du ${jour(bDebut)} au ${jour(bFin)}` : (bDebut || bFin) ? `Depuis ${jour(bDebut) || jour(bFin)}` : t('dialer:callHistory.period.all');
    downloadCsv(`${nom}.csv`, toCsv(headers, lines));
    if (result.capped) {
      setExportCapped({ count: result.items.length, total: result.total });
      toast.warning(t('dialer:callHistory.export.capped', { count: result.items.length, total: result.total }));
    }
  }, [filter, sortParam, total, period, customStart, customEnd, t, prospectByPhone, statusLabelOf]);

  // ── Filter pills ─────────────────────────────────────────────────────────
  const clearAllFilters = () => {
    setPeriod(DEFAULT_HISTORY_PERIOD); setCustomStart(''); setCustomEnd('');
    setSelectedStatuses([]); setSelectedSources([]); setMinDuration('0');
    setDirection('all'); setAgentEmail(''); setSearchInput(''); setSearch('');
  };
  const pills = [];
  if (period !== DEFAULT_HISTORY_PERIOD) pills.push({ id: 'period', label: periodLabel(period), onRemove: () => { setPeriod(DEFAULT_HISTORY_PERIOD); setCustomStart(''); setCustomEnd(''); } });
  selectedStatuses.forEach((s) => pills.push({ id: `status-${s}`, label: outcomeLabel(s, t), onRemove: () => setSelectedStatuses((v) => v.filter((x) => x !== s)) }));
  selectedSources.forEach((s) => pills.push({ id: `list-${s}`, label: s, onRemove: () => setSelectedSources((v) => v.filter((x) => x !== s)) }));
  if (agentEmail) pills.push({ id: 'user', label: t('dialer:callHistory.filters.userPill', { name: users.find((u) => u.email === agentEmail)?.name || agentEmail }), onRemove: () => setAgentEmail('') });
  if (minDuration !== '0') pills.push({ id: 'duration', label: `≥ ${minDuration} s`, onRemove: () => setMinDuration('0') });
  if (direction !== 'all') pills.push({ id: 'direction', label: t(`dialer:callHistory.direction.${direction}`), onRemove: () => setDirection('all') });
  if (search.trim()) pills.push({ id: 'search', label: `« ${search.trim()} »`, onRemove: () => { setSearchInput(''); setSearch(''); } });

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns = [
    ...(showUsers ? [{
      id: 'user', header: t('dialer:callHistory.columns.user'), hideable: true,
      cell: (call) => <span className="text-xs whitespace-nowrap">{call.agent_name || call.agent_email || '—'}</span>,
    }] : []),
    {
      id: 'contact', header: t('dialer:callHistory.columns.contact'),
      cell: (call) => (
        <div className="min-w-0">
          <p className="font-medium truncate leading-tight">
            {call.prospect_name || '—'}
            {resolveDirection(call) === 'inbound' && (
              <span className="ml-2 align-middle text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-600" data-testid="tag-appel-entrant">
                {t('dialer:callHistory.inboundTag')}
              </span>
            )}
          </p>
          {call.prospect_company && <p className="text-xs text-muted-foreground truncate leading-tight mt-0.5">{call.prospect_company}</p>}
        </div>
      ),
    },
    {
      id: 'linkedin', header: t('dialer:callHistory.columns.linkedin'), hideable: true,
      cell: (call) => (linkedinOf(call)
        ? <a href={linkedinOf(call)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-blue-600 hover:underline">{t('dialer:callHistory.linkedinProfile')}</a>
        : <span className="text-muted-foreground/40 text-xs">—</span>),
    },
    {
      id: 'calls', header: t('dialer:callHistory.columns.callsCount'), hideable: true, align: 'right',
      cell: (call) => <span className="text-xs font-mono">{attemptsOf(call) ?? '—'}</span>,
    },
    {
      id: 'status', header: t('common:field.status'),
      cell: (call) => (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <StatusBadge status={resolveCallStatus(call)} t={t} />
          <CallScreeningTag call={call} />
          {callFailureLabel(call, t) && (
            <span className="text-xs text-muted-foreground" title={call.sip_response_code ? `SIP ${call.sip_response_code}` : undefined} data-testid="call-failure-reason">
              {callFailureLabel(call, t)}
            </span>
          )}
          {canOfferCorrection(call) && (
            <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs" data-testid="corriger-qualification"
              onClick={(e) => { e.stopPropagation(); setToCorrect(call); }}>
              {t('dialer:callHistory.correct.button')}
            </Button>
          )}
        </div>
      ),
    },
    { id: 'direction', header: t('dialer:callHistory.columns.direction'), hideable: true, cell: (call) => <DirectionIcon direction={resolveDirection(call)} t={t} /> },
    { id: 'duration', header: t('pages:callHistory.columns.duration'), hideable: true, align: 'right', cell: (call) => <span className="font-mono tabular-nums"><DurationCell call={call} /></span> },
    {
      id: 'from', header: t('dialer:callHistory.columns.from'), hideable: true,
      cell: (call) => { const n = call.from_number || call.agent_number || call.caller_id || ''; return <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">{n ? formatPhoneNumber(n) : '—'}</span>; },
    },
    {
      id: 'to', header: t('dialer:callHistory.columns.to'), hideable: true,
      cell: (call) => { const n = call.prospect_phone || call.to_number || ''; return <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">{n ? formatPhoneNumber(n) : '—'}</span>; },
    },
    { id: 'start', header: t('dialer:callHistory.columns.startEnd'), sortable: true, cell: (call) => <StartEnd call={call} /> },
    { id: 'recording', header: t('dialer:callHistory.columns.recording'), hideable: true, cell: (call) => <RecordingCell call={call} /> },
    {
      id: 'share', header: <span className="sr-only">{t('dialer:callHistory.columns.share')}</span>,
      cell: (call) => (
        <button
          type="button"
          data-testid="copier-lien-ligne"
          title={idLienCopie === call.id ? t('dialer:callHistory.copyLink.copied') : t('dialer:callHistory.copyLink.copy')}
          aria-label={t('dialer:callHistory.copyLink.ariaLabel', { name: call.prospect_name || '' }).trim()}
          onClick={(e) => {
            e.stopPropagation();
            const lien = lienAppel(call.id);
            void copyText(lien).then((ok) => {
              // A refused copy says so and shows the link (CEO-14).
              if (!ok) { toast.error(t('common:copyLink.manual'), { description: lien }); return; }
              setIdLienCopie(call.id); toast.success(t('common:copyLink.copied'));
              setTimeout(() => setIdLienCopie((v) => (v === call.id ? null : v)), 1800);
            });
          }}
          className="w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-accent transition-colors"
        >
          {idLienCopie === call.id ? <Check className="w-3.5 h-3.5 text-green-600" aria-hidden /> : <Link2 className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />}
        </button>
      ),
    },
  ];

  const status = query.isPending ? 'loading' : query.isError && !query.data ? 'error' : 'ready';
  const periodDates = (() => {
    const [d1, d2] = getPeriodRange(period, customStart, customEnd);
    const fmt = (d) => (d ? new Date(d).toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) : null);
    if (!d1 && !d2) return t('dialer:callHistory.period.noRestriction');
    if (d1 && d2) return `${fmt(d1)} - ${fmt(d2)}`;
    return fmt(d1) || fmt(d2);
  })();

  return (
    <>
    <div className="space-y-3">
      {/* Faceted filter bar; the chosen filters show as pills above the table. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="historique-filtres">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-48 h-9" aria-label={t('pages:callHistory.filters.dateRange')}>
            <Calendar className="w-3.5 h-3.5 text-muted-foreground mr-1.5 shrink-0" aria-hidden />
            <SelectValue placeholder={t('pages:callHistory.filters.dateRange')} />
          </SelectTrigger>
          <SelectContent>
            {periodOptions.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* #79 -- the exact dates of the chosen period. */}
        <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="periode-dates">{periodDates}</span>
        {period === 'custom' && (
          <div className="flex items-center gap-1.5">
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-9 w-36 text-sm" aria-label={t('dialer:callHistory.filters.customStart')} />
            <span className="text-muted-foreground text-sm" aria-hidden>→</span>
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 w-36 text-sm" aria-label={t('dialer:callHistory.filters.customEnd')} />
          </div>
        )}

        <StatusFilterPopover selectedStatuses={selectedStatuses} onChange={setSelectedStatuses} statusOptions={statusOptions} t={t} />

        {(sources || []).length > 0 && (
          <ListFilterPopover sources={sources || []} prospectsBySource={prospectsBySource || {}} selectedSources={selectedSources} onChange={setSelectedSources} t={t} />
        )}

        {showUsers && (
          <Select value={agentEmail || '__all__'} onValueChange={(v) => setAgentEmail(v === '__all__' ? '' : v)}>
            <SelectTrigger className="w-44 h-9" aria-label={t('dialer:callHistory.filters.user')}>
              <User className="w-3.5 h-3.5 text-muted-foreground mr-1.5 shrink-0" aria-hidden />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t('dialer:callHistory.filters.allUsers')}</SelectItem>
              {users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={minDuration} onValueChange={setMinDuration}>
          <SelectTrigger className="w-32 h-9" aria-label={t('dialer:callHistory.filters.minDuration')}>
            <Clock className="w-3.5 h-3.5 text-muted-foreground mr-1.5 shrink-0" aria-hidden />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MIN_DURATION_OPTIONS.map(({ value, label }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="w-36 h-9" aria-label={t('dialer:callHistory.direction.label')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('dialer:callHistory.direction.all')}</SelectItem>
            <SelectItem value="outbound">{t('dialer:callHistory.direction.outbound')}</SelectItem>
            <SelectItem value="inbound">{t('dialer:callHistory.direction.inbound')}</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" aria-hidden />
          <Input
            placeholder={t('dialer:callHistory.filters.searchPlaceholder')}
            aria-label={t('dialer:callHistory.filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
          {searchInput && (
            <button type="button" aria-label={t('dialer:callHistory.filters.clearSearch')} className="absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-muted-foreground hover:text-foreground" onClick={() => { setSearchInput(''); setSearch(''); }}>
              <X className="w-3.5 h-3.5" aria-hidden />
            </button>
          )}
        </div>

        <div className="ml-auto" />
        <Button variant="outline" size="sm" onClick={() => void handleExport()} disabled={!!exporting || status !== 'ready' || total === 0} className="gap-1.5" data-testid="historique-export">
          <Download className="w-4 h-4" aria-hidden />
          {exporting
            ? t('dialer:callHistory.export.progress', { loaded: exporting.loaded, total: Math.min(exporting.total ?? EXPORT_CAP, EXPORT_CAP) })
            : t('dialer:callHistory.export.button')}
        </Button>
      </div>

      {exportCapped && (
        <p role="status" data-testid="historique-export-plafond" className="rounded-vf border px-3 py-2 text-[12px]" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)', color: 'var(--vf-text-2)' }}>
          {t('dialer:callHistory.export.capped', { count: exportCapped.count, total: exportCapped.total })}
        </p>
      )}

      {listsBeyondCap.status !== 'idle' && !(listsBeyondCap.status === 'ready' && listsBeyondCap.data.complete) && (
        <p role="status" data-testid="historique-listes-plafond" className="flex flex-wrap items-center gap-2 rounded-vf border px-3 py-2 text-[12px]" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)', color: 'var(--vf-text-2)' }}>
          {listsBeyondCap.status === 'loading' && t('dialer:callHistory.listFilter.loading')}
          {listsBeyondCap.status === 'ready' && (listsBeyondCap.data.total !== null
            ? t('dialer:callHistory.listFilter.partial', { shown: listsBeyondCap.data.loaded, total: listsBeyondCap.data.total })
            : t('dialer:callHistory.listFilter.partialUnknown', { shown: listsBeyondCap.data.loaded }))}
          {listsBeyondCap.status === 'error' && (
            <>
              <span>{t('dialer:callHistory.listFilter.error')}</span>
              {errorCode(listsBeyondCap.error) && <span className="font-mono text-[11px]" style={{ color: 'var(--vf-text-3)' }}>{errorCode(listsBeyondCap.error)}</span>}
              <Button type="button" variant="outline" size="sm" onClick={listsBeyondCap.retry}>{t('common:actions.retry')}</Button>
            </>
          )}
        </p>
      )}

      <DataTable
        caption={t('pages:callHistory.title')}
        columns={columns}
        rows={rows}
        getRowId={(call) => String(call.id)}
        status={status}
        error={{
          message: t('dialer:callHistory.loadError'),
          code: errorCode(query.error),
          onRetry: () => void query.refetch(),
        }}
        sort={sort}
        onSortChange={(s) => setSort(s ?? { id: 'start', dir: 'desc' })}
        filters={pills}
        onClearFilters={clearAllFilters}
        empty={period === 'all'
          ? { title: t('dialer:callHistory.empty.none'), action: <Button size="sm" onClick={() => navigate('/ParallelDialer')}>{t('dialer:callHistory.empty.call')}</Button> }
          : { title: t('dialer:callHistory.empty.inPeriod', { period: periodLabel(period).toLowerCase() }), action: <Button size="sm" variant="outline" onClick={() => setPeriod('all')}>{t('dialer:callHistory.empty.allPeriod')}</Button> }}
        pagination={{ page, pageSize: HISTORY_PAGE_SIZE, total, onPageChange }}
        onRowClick={ouvrirFiche}
        storageKey="call-history"
      />
    </div>

    {/* #76 -- call detail (reached through the call link). */}
    <Dialog open={!!callDetail} onOpenChange={(o) => { if (!o) setCallDetail(null); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {callDetail && (() => {
          const c = callDetail;
          const debut = c.created_date || c.started_at ? new Date(c.created_date || c.started_at) : null;
          const secondes = Number(c.duration_seconds || 0);
          let fin = c.ended_at ? new Date(c.ended_at) : null;
          if ((!fin || isNaN(fin.getTime()) || (debut && fin <= debut)) && debut && secondes > 0) fin = new Date(debut.getTime() + secondes * 1000);
          const dt = (d) => (d && !isNaN(d.getTime())
            ? d.toLocaleString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
            : '—');
          const ligne = (libelle, valeur) => (
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{libelle}</span>
              <span className="text-sm text-foreground tabular-nums">{valeur || '—'}</span>
            </div>
          );
          const objections = detecterObjections(c);
          return (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Phone className="w-4 h-4" aria-hidden />
                  {c.prospect_name || t('dialer:callHistory.detail.fallbackTitle')}
                  {c.prospect_company && <span className="text-sm font-normal text-muted-foreground">, {c.prospect_company}</span>}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
                {ligne(t('dialer:callHistory.detail.status'), <span className="inline-flex items-center gap-2"><StatusBadge status={getEffectiveOutcome(c)} t={t} /><CallScreeningTag call={c} /></span>)}
                {callFailureLabel(c, t) && ligne(t('dialer:callFailure.label'), callFailureLabel(c, t))}
                {isTenantAdmin && typeof c.prospect_wait_ms === 'number' && ligne(
                  t('dialer:callHistory.detail.prospectWait'),
                  t('dialer:callHistory.detail.prospectWaitValue', { seconds: (c.prospect_wait_ms / 1000).toLocaleString(currentLocale(), { maximumFractionDigits: 1 }) }),
                )}
                {ligne(t('dialer:callHistory.detail.duration'), formatDurationClock(c.duration_seconds))}
                {ligne(t('dialer:callHistory.detail.agent'), c.agent_name || c.agent_email)}
                {ligne(t('dialer:callHistory.detail.from'), formatPhoneNumber(c.from_number || c.agent_number || ''))}
                {ligne(t('dialer:callHistory.detail.to'), formatPhoneNumber(c.prospect_phone || c.to_number || ''))}
                {ligne(t('dialer:callHistory.detail.direction'), c.direction === 'inbound' ? t('dialer:callHistory.direction.inbound') : t('dialer:callHistory.direction.outbound'))}
                {ligne(t('dialer:callHistory.detail.start'), dt(debut))}
                {ligne(t('dialer:callHistory.detail.end'), dt(fin))}
              </div>

              {canOfferCorrection(c) && (
                <div className="mt-3">
                  <Button type="button" size="sm" variant="outline" onClick={() => setToCorrect(c)}>{t('dialer:callHistory.correct.button')}</Button>
                </div>
              )}

              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{t('dialer:callHistory.detail.recording')}</p>
                <RecordingCell call={c} />
              </div>

              {c.ai_summary && (
                <div className="mt-5 rounded-lg border border-border bg-muted/30 p-3">
                  <p className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" aria-hidden /> {t('dialer:callHistory.detail.aiSummary')}</p>
                  <p className="text-sm text-foreground/90">{c.ai_summary}</p>
                </div>
              )}

              {objections.length > 0 && (
                <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3" data-testid="popup-objections">
                  <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" aria-hidden /> {t('dialer:callHistory.detail.objectionsDetected', { count: objections.length })}
                  </p>
                  <div className="space-y-2">
                    {objections.map((o, i) => (
                      <div key={i} className="text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">{t(`pages:objections.families.${o.famille}`)}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${o.statut === 'passee' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                            {o.statut === 'passee' ? t('dialer:callHistory.detail.objectionPassed') : t('dialer:callHistory.detail.objectionNotPassed')}
                          </span>
                          {o.horodatage && <span className="text-[11px] font-mono text-muted-foreground">{o.horodatage}</span>}
                        </div>
                        <p className="text-foreground/90 mt-1">{o.resume}</p>
                        {o.reponse && <p className="text-xs text-muted-foreground mt-0.5">{t('dialer:callHistory.detail.objectionResponse', { response: o.reponse })}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {c.transcription && (
                <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
                  <p className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" aria-hidden /> {t('dialer:callHistory.detail.transcription')}</p>
                  <p className="text-sm text-foreground/90 whitespace-pre-line">{c.transcription}</p>
                </div>
              )}

              <div className="mt-5 flex items-center gap-2">
                <CopyLink url={lienAppel(c.id)} label={t('dialer:callHistory.copyLink.copy')} />
                <span className="text-xs text-muted-foreground font-mono truncate">{c.id}</span>
              </div>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>

    <CorrectQualificationDialog call={toCorrect} onOpenChange={(open) => { if (!open) setToCorrect(null); }} onCommitted={refreshCallDetail} />

    <ProspectDetailPanel
      prospect={selectedProspectForDetail}
      isOpen={!!selectedProspectForDetail}
      onClose={() => setSelectedProspectForDetail(null)}
    />
    </>
  );
}
